/**
 * UTILITY BELT
 *
 * A minimal utility belt file which contains various general use helpers.
 *
 * @version 0.1
 */

import {hashHistory} from 'react-router';
import {forIn, random, toLower} from 'lodash';
import storage from './storage';
import commonActions from './../api/Common.actions';
import {
    ALLOWED_FORMATTING_HTML,
    CATEGORY_DELETION_LINKED_MAX_COUNT,
    DELIVERY_ICONS_EMAIL_ADDRESSES_ALLOWED,
    DEVELOPMENT_ENVIRONMENT,
    ENTITIES_FILTER_ALL_ENTITIES,
    ENTITIES_FILTER_SPECIFIC,
    LINK_TYPE_ALERT,
    LINK_TYPE_FEED,
    LOCALES,
    SANCTION_TYPE_ES_EMBARGOS,
    SANCTION_TYPE_ES_FRONTEIRA_INTERNATIONAL,
    SANCTION_TYPE_PEP,
    SANCTION_TYPE_PEP_ES,
    SANCTION_TYPE_SANCTION_LISTS,
    SANCTION_TYPE_SANLST,
    SANCTION_TYPE_SANLST_SANCTIONS,
    SANCTION_TYPE_SANLST_TITLE,
    SANCTION_TYPE_WATCHB,
    USER_SESSION_EXPIRY_TIME,
    VISIBLE_RISK_PERSPECTIVES
} from './../constants';

import errorMessagesActions from './../reusable/MessagePopup/redux/MessagePopup.actions';
import categoriesApi from './../api/Categories.api';
import {APP_LANGUAGES} from "../constants";

var utils = {

    /**
     * TRANSLATIONS
     */

    getPguid(pguid, selectedLanguage) {
        return LOCALES[selectedLanguage].translations["pguidTranslation_" + pguid];
    },

    /**
     * ERROR HANDLING AND NAVIGATION
     */

    goTo404Page() {
        errorMessagesActions.setMessages({
            messages: [
                {
                    titleId:     'General_CoreFunctionality_Error_error.404Title',
                    messageType: 'system-error',
                    messageId:   'General_CoreFunctionality_Error_error.404Message'
                }
            ]
        });

        hashHistory.push('/error');
    },

    /**
     * TIME METHODS
     */

    /**
     * Converts a date represented as Unix time days to one represented as Unix time milliseconds.
     *
     * @param   {string|number} days   The Unix time days.
     * @return  {number}               The date as Unix time milliseconds.
     */
    convertDaysToMilliseconds: function(days) {
        return Number(days) * 24 * 3600 * 1000;
    },

    removeUTCOffset(unixDate) {
        let milliseconds = unixDate,
            offset = (new Date()).getTimezoneOffset() * 60 * 1000; // The offset is given in minutes

        // The offset is SUBTRACTED (the offset is given as negative
        // value when it's positive and vice-versa) from the date given
        // in milliseconds so that it gets in the UTC format.
        return milliseconds + offset;
    },


    /**
     * OBJECT MANIPULATION METHODS
     */

    /**
     * Deep clones a given object.
     *
     * @param   {Object} obj   The object to be cloned.
     * @return  {Object}       The newly created clone.
     */
    deepClone: function(obj) {
        return JSON.parse(JSON.stringify(obj));
    },


    /**
     * COMPARISON FUNCTIONS
     */

    /**
     * Compares if two given parameters of any value are deeply equal.
     *
     * @param   a           The first object to compare.
     * @param   b           The second object to compare.
     * @return  {boolean}   True if the objects are deeply equal or false otherwise.
     */
    isDeepEqual: function(a, b) {

        if (Object.prototype.toString.call(a) !== Object.prototype.toString.call(b)) {
            return false;
        }

        if (utils.isArray(a) || utils.isObject(a)) {

            for (let key in a) {
                if (utils.isArray(a[key]) || utils.isObject(a[key])) {

                    if (!utils.isDeepEqual(a[key], b[key])) {
                        return false;
                    }

                } else if (a[key] !== b[key]) {
                    return false;
                }
            }

        } else if (a !== b) {
            return false;
        }

        return true;

    },

    /**
     * Checks if an object is empty.
     *
     * @param   {Object}  obj   The object to be checked.
     * @return  {boolean}       Returns true if the object is empty or false otherwise.
     */
    isEmptyObject(obj) {
        return utils.isDeepEqual(obj, {});
    },


    /**
     * ARRAY OPERATIONS
     */

    /**
     * Returns the position of a given value in an array. Returns -1 in case the value was not found.
     *
     * @param               value             The value to be searched in the array.
     * @param   {Array}     array             The array of values.
     * @param   {string}    propertyToCheck   If the array is a collection of objects, you can specify to
     *                                        search the value against a certain property of those objects.
     *          {function}  alterValues       Custom callback that alters the checked values for a more flexible match.
     * @return  {number}                      The array position of the found value or -1 if the value was not found.
     */
    inArray: function(value, array, propertyToCheck, alterValues) {

        for (let i = 0; i < array.length; i++) {

            let arrayValue = typeof propertyToCheck === 'string' ? array[i][propertyToCheck] : array[i];

            if (typeof alterValues === 'function') {
                arrayValue = alterValues(arrayValue);
                value = alterValues(value);
            }

            if (arrayValue === value) {
                return i;
            }

        }

        return -1;

    },

    /**
     * Searches for a value in an array using the deep equal comparison.
     *
     * @param   {*}      value   The value to be searched inside the array.
     * @param   {Array}  array   The array of values.
     * @return  {number}         The array position of the found value or -1 if the value was not found.
     */
    inArrayDeepSearch(value, array) {
        for (let i = 0; i < array.length; i++) {
            if (utils.isDeepEqual(value, array[i])) {
                return i;
            }
        }

        return -1;
    },

    /**
     * Returns the difference of two arrays.
     *
     * @param   {Array}     initial       The initial array.
     * @param   {Array}     subtracted    The subtracted array.
     * @param   {boolean}   removalMode   If true, the missing elements from the subtracted array will not be added.
     * @return  {Array}                   The difference of the two arrays.
     */
    arrayDiff: function(initial, subtracted, removalMode = false) {

        let diff = [];

        // Shallow copy the elements of the array
        for (let i = 0; i < initial.length; i++) {
            diff[i] = initial[i];
        }

        // Do the subtraction
        for (let i = 0; i < subtracted.length; i++) {
            let index = utils.inArray(subtracted[i], diff);

            if (index > -1) {
                diff.splice(index, 1);
            } else if (!removalMode) {
                diff.push(subtracted[i]);
            }
        }

        return diff;

    },


    /**
     * TYPE CHECKING
     */

    /**
     * Checks if a given value is an array.
     *
     * @param   value       The value to be checked.
     * @return  {boolean}   True if the given value is an array or false otherwise.
     */
    isArray: function(value) {
        return Object.prototype.toString.call(value) === '[object Array]';
    },

    /**
     * Checks if a given value is an object.
     *
     * @param    value       The value to be checked.
     * @returns  {boolean}   True if the given value is an object or false otherwise.
     */
    isObject: function(value) {
        return Object.prototype.toString.call(value) === '[object Object]';
    },


    /**
     * DOM PARSING UTILITIES
     */

    /**
     * Checks whether an HTML string contains only formatting tags:
     * 'b', 'em', 'i', 'small', 'strong', 'sub', 'sup', 'ins', 'del', 'mark'.
     *
     * @param   {string}  html   The HTML string to be checked.
     * @return  {boolean}        True if the string is an HTML formatting string or false otherwise.
     */
    isHTMLFormattingString: function(html) {

        if (typeof html !== 'string') {
            return false;
        }

        // /<\/?[a-zA-Z0-9]*(\s+(\S+)=["']?((?:.(?!["']?\s+(?:\S+)=|[>"']))+.)["']?)*\s*\/?>/g
        let htmlTagsRegExp = /<\/?[^<>]+\/?>/g,
            allowedTags    = ALLOWED_FORMATTING_HTML,
            tags           = html.match(htmlTagsRegExp);

        if (utils.isArray(tags)) {

            // The opening and closing tag forms are the permitted variants of the formatting tags
            let permittedTagVariants = '^<\/?(';

            for (let i = 0; i < allowedTags.length; i++) {
                permittedTagVariants += ( allowedTags[i] + (i === allowedTags.length - 1 ? ')>$' : '|') );
            }

            permittedTagVariants = new RegExp(permittedTagVariants);

            // Check if the tag is allowed
            for (let i = 0; i < tags.length; i++) {

                if (!permittedTagVariants.test(tags[i])) {
                    return false;
                }

            }

        }

        return true;

    },

    /**
     * Parses an HTML formatting string and creates the appropriate innerHTML string from it.
     * @see {@link isHTMLFormattingString} for more info on what an HTML formatting string is.
     *
     * @param   {string}   html   The HTML string to be parsed.
     * @return  {string}          The parsed HTML  string.
     */
    parseHTMLFormattingString: function(html, value) {

        if (utils.isHTMLFormattingString(html)) {

            if (typeof value !== 'undefined' && value !== null) {
                html = html.replace(/%v/g, value.toString());
            }

            return html;

        }

        return '';
    },


    /**
     * STRING FORMATTING METHODS
     */

    /**
     * Capitalizes all words in a phrase.
     *
     * @param   {string} phrase   Phrase whose words will be capitalized.
     * @return  {string}          The capitalized phrase.
     */
    capitalizeWords: function(phrase) {

        if (typeof(phrase) !== 'string') {
            return '';
        }

        let words = phrase.split(' ');

        for (let i = 0; i < words.length; i++) {
            words[i] = words[i].toLowerCase();
            words[i] = words[i].charAt(0).toUpperCase() + words[i].substr(1);
        }

        return words.join(' ');

    },

    /**
     * MISCELLANEOUS
     */

    /**
     * UUID generator. Algorithm written by Stack Overflow user "broofa":
     * @see {@link http://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript#2117523}
     *
     * @return  {string}   The generated UUID.
     */
    generateUUID: function() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            var r = Math.random() * 16 | 0,
                v = c == 'x' ? r : r & 0x3 | 0x8;
            return v.toString(16);
        });
    },

    /**
     * Random hash generator. Algorithm written by Stack Overflow user "broofa":
     * @see {@link http://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript#2117523}
     *
     * @return  {string}   The generated UUID.
     */
    generateRandomHash: function() {
        return 'xxx-xxx-xxx'.replace(/[xy]/g, function(c) {
            var r = Math.random() * 16 | 0,
                v = c == 'x' ? r : r & 0x3 | 0x8;
            return v.toString(16);
        });
    },


    /**
     * Checks whether a given value is a valid URL.
     *
     * @param   {string} url   The value to be checked.
     * @return  {boolean}      True or false depending if the value is a valid URL or not.
     */
    isValidUrl: function(url) {

        return true;

        // let urlRegExp =
        //         /^(http[s]?\:\/)?\/(([a-zA-Z0-9][-.\w]+)\/)*([a-zA-Z0-9][-.\w]+)?(\?[\w-]+=[\w]+(&[\w-]+=[\w]+)*)?$/;
        //
        // return typeof url === 'string' && url.match(urlRegExp) ? true : false;

    },

    /**
     * Fetches query parameter values from URLs.
     * Source: {@link
     * http://stackoverflow.com/questions/901115/how-can-i-get-query-string-values-in-javascript#answer-901144}
     *
     * @param   {string} name   The name of the parameter.
     * @param   {string} url    The URL from which to extract the paramter value.
     * @return  {string}        The query param value.
     */
    getQueryParam(name, url) {
        if (!url) url = window.location.href;
        name = name.replace(/[\[\]]/g, "\\$&");
        var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
            results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return '';
        return decodeURIComponent(results[2].replace(/\+/g, " "));
    },

    getAllUrlParams(url = null) {
        if (!url) {
            url = window.location.href;
        }
        const allParams = url.substr(url.indexOf("?") + 1);
        const params = allParams.split("&").map(param => param.split("="));
        let obj = {};
        params.forEach(param => {
            obj[param[0]] = param[1]
        })
        return obj;
    },

    /**
     * LOGGING METHODS
     */

    logError: function(error, description = '') {

        if (DEVELOPMENT_ENVIRONMENT) {
            console.log('--------------------------------------------------');
            console.log('ERROR: ');

            if (description) {
                console.log(description);
            }

            console.log(error);
            console.log('--------------------------------------------------');
        }

    },

    /**
     * Sorting method used for internal stores when using view selected items
     * Sort order can be 'asc' or anything else for descending
     *
     * @param {array} array the initial array to be sorted
     * @param {string} fieldName the field within the object to be sorted
     * @param {string} sortOrder 'asc' for ascending sort, or anything else for descending
     *
     * @return {array} a new array with the sorted elements
     *
     */
    sortSelectedItemsArray(array, fieldName, sortOrder) {

        if (array.length > 0) {

            let selectedItems = [...array];

            selectedItems.sort(function(a, b) {

                let filedA = a[fieldName],
                    fieldB = b[fieldName];

                if (filedA === fieldB) {
                    return 0;
                }

                if (sortOrder === 'asc') {
                    return filedA < fieldB ? -1 : 1;
                } else {
                    return filedA < fieldB ? 1 : -1;
                }

            });

            return selectedItems;

        }

        return [];

    },

    /**
     * Alphabetically sort array of objects by object key
     *
     * @param {array} array of objects
     * @param {key} common object key to filter on
     *
     *
     * @return {array} alphabetically sorted array
     *
     */
    sortArrayByKey(array, key) {
        return array.sort(function(a, b) {
            let x = a[key];
            let y = b[key];
            x = x.toUpperCase();
            y = y.toUpperCase();
            return ((x < y) ? -1 : ((x > y) ? 1 : 0));
        });
    },


    /**
     * Filtering method used for internal stores when using view selected items
     *
     * @param {string} filter the field within the object which needs to be present
     * @param {array} items the initial array to be sorted
     *
     * @return {array} a new array with the filtered elements
     *
     */
    filterArrayByField(filter, items) {
        if (filter) {
            let finalList = [];
            for (let i = 0 ; i < items.length ; i++) {
                if (utils.inArray(filter, items[i].categoryIDs) != -1) {
                    finalList.push(items[i]);
                }
            }
            return finalList;
        } else {
            return items;
        }
    },


    /**
     * Filters internal table store items by status.
     *
     * @param   {string} status   The status
     * @param   {Array}  items    The array of items to filter.
     * @return  {Array}           The filtered array of items.
     */
    filterByStatus(status, items) {
        switch (status) {
            case 'active':
                return items.filter(item => item.active);
            case 'non-active':
                return items.filter(item => !item.active);
            default:
                return items;
        }
    },


    /**
     * Filtering method used to merge two arrays by field
     *
     * @param {string} field the field within the object which needs to be present
     * @param {array} a the first array to pick up
     * @param {array} b the second array to pick up
     *
     * @return {array} a new array with the merged elements
     *
     */
    mergeArraysByField(field, a, b) {
        let newList = [...a];
        for (let i = 0 ; i < b.length ; i++) {
            if (utils.inArray(b[i].id, newList, field) == -1) {
                newList.push(b[i]);
            }
        }
        return newList;
    },

    /**
     * Filtering method used to exclude an array subset
     *
     * @param {string} field the field within the object which needs to be present
     * @param {array} initial the first array to pick up
     * @param {array} exclusion the array which will be excluded
     *
     * @return {array} a new array with the merged elements
     *
     */
    subsetArrayByField(field, initial, exclusion) {
        let newList = [];
        for (let i = 0 ; i < initial.length ; i++) {
            if (utils.inArray(initial[i].id, exclusion, field) == -1) {
                newList.push(initial[i]);
            }
        }
        return newList;
    },


    highlightSubStrings(searchStr, str) {
        let searchStrLen = searchStr.length;
        if (searchStrLen == 0) {
            return str;
        }
        let startIndex = 0, index, indices = [];
        let tmpStr = str.toLowerCase();
        let tmpSearchStr = searchStr.toLowerCase();
        while ((index = tmpStr.indexOf(tmpSearchStr, startIndex)) > -1) {
            indices.push(index);
            startIndex = index + searchStrLen;
        }

        if (indices.length == 0) return str;

        let newStr = '';
        let lastIndex = 0;

        for (let i =0 ; i < indices.length ; i++) {
            if (i==0) {
                newStr = newStr + str.substring(lastIndex, indices[i]) + '<strong>'+str.substring(indices[i], indices[i] + searchStrLen)+ '</strong>';
            } else {
                newStr = newStr + str.substring(lastIndex + searchStrLen,indices[i]) + '<strong>'+str.substring(indices[i], indices[i] + searchStrLen)+ '</strong>';
            }
            lastIndex = indices[i];
        }

        newStr = newStr + str.substring(lastIndex + searchStrLen, str.length);
        return newStr;
    },

    /**
     * Filtering method used to cut strings short and add ellipsis
     *
     * @param {string} content to be processed
     * @param {number} breakPoint where the ellipsis should be added
     *
     * @return {string} new string with ellipsis at the end
     *
     */
    toEllipsis(content, breakPoint) {
        let ellipsis = "...";
        let contentWithEllipsis = content ? content : '';
        let receivedBreakPoint = breakPoint ? breakPoint : 150;
        if (contentWithEllipsis.length >= receivedBreakPoint) {
            contentWithEllipsis = contentWithEllipsis.substring(0, receivedBreakPoint);
            let lastSpaceInString = contentWithEllipsis.lastIndexOf(" ") + 1;
            contentWithEllipsis = contentWithEllipsis.substring(0, lastSpaceInString).concat(ellipsis);
        }

        return contentWithEllipsis;
    },


    createPestleBulletMap() {
        return {
            "political":     {
                "visible": false,
                "HIGH": false,
                "MEDIUM": false,
                "LOW": false
            },
            "economic": {
                "visible": false,
                "HIGH": false,
                "MEDIUM": false,
                "LOW": false
            },
            "social": {
                "visible": false,
                "HIGH": false,
                "MEDIUM": false,
                "LOW": false
            },
            "technological": {
                "visible": false,
                "HIGH": false,
                "MEDIUM": false,
                "LOW": false
            },
            "legal": {
                "visible": false,
                "HIGH": false,
                "MEDIUM": false,
                "LOW": false
            },
            "environmental": {
                "visible": false,
                "HIGH": false,
                "MEDIUM": false,
                "LOW": false
            }
        };

    },

    parsePestleResponse(pestles) {

        // The default PESTLE parsing algorithm that's most common
        let pestleMap = utils.createPestleBulletMap();
        for (let i = 0; i < pestles.length; i++) {
            pestleMap[pestles[i].name][pestles[i].scoreLevel] = true;
            pestleMap[pestles[i].name]["visible"] = true;
        }

        return pestleMap;
    },

    /**
     * USER SESSION
     */

    setUserSession: function (sessionData) {
        storage.setObject('userSession', Object.assign({}, sessionData, {
            uuid: utils.generateUUID(),
            time: new Date()
        }));
    },

    isUserSessionValid: function () {
        let userSession = storage.getObject('userSession');

        if (userSession) {
            if (!userSession.rememberMe) {
                return true;
            }

            if (userSession.time && new Date() - Date.parse(userSession.time) < USER_SESSION_EXPIRY_TIME) {
                return true;
            }
        }

        return false;
    },

    clearUserSession: function () {
        storage.remove('userSession');
    },

    getUserSessionData(key) {
        let userSession = storage.getObject('userSession');
        if (userSession) {
            return userSession[key] ? userSession[key] : null;
        }

        return null;
    },

    /**
     * Fetches query parameter values from URLs.
     * Source: {@link https://stackoverflow.com/questions/4068373/center-a-popup-window-on-screen}
     *
     * @param   {string} url    url to open in the popup
     * @param   {string} title  popup title
     * @param   {string} w      popup width
     * @param   {string} h      popup height
     */
    openNewWindowAsCenteredPopup(url, title, w, h) {
        // Fixes dual-screen position                         Most browsers      Firefox
        let dualScreenLeft = window.screenLeft != undefined ? window.screenLeft : window.screen.left;
        let dualScreenTop = window.screenTop != undefined ? window.screenTop : window.screen.top;

        let width = window.innerWidth ? window.innerWidth :
            document.documentElement.clientWidth ? document.documentElement.clientWidth : window.screen.width;
        let height = window.innerHeight ? window.innerHeight :
            document.documentElement.clientHeight ? document.documentElement.clientHeight : window.screen.height;

        let left = ((width / 2) - (w / 2)) + dualScreenLeft;
        let top = ((height / 2) - (h / 2)) + dualScreenTop;
        let newWindow = window.open(url, title,
            'scrollbars=yes, width=' + w + ', height=' + h + ', top=' + top + ', left=' + left);

        // Puts focus on the newWindow
        if (window.focus) {
            newWindow.focus();
        }
    },

    /**
     * Checks if string has valid emails
     * @param value
     * @returns {boolean}
     */
    isEmailAddressValid(value) {

        let emailAddresses = value.split(',');
        if (emailAddresses.length > DELIVERY_ICONS_EMAIL_ADDRESSES_ALLOWED) {
            return false;
        }

        let emailAddressesValidity = true;
        for (let i = 0; i < emailAddresses.length; i++) {
            emailAddresses[i] = emailAddresses[i].trim();
            emailAddressesValidity = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+){0,10}?$/.test(emailAddresses[i]);
            if (!emailAddressesValidity) { break;}
        }
        return emailAddressesValidity;
    },

    /**
     * Triggers window resize. Used for repainting the C3 chart
     */
    triggerWindowResize() {
        const evt = document.createEvent("HTMLEvents");
        evt.initEvent('resize', true, false);
        window.dispatchEvent(evt);
    },

    /**
     * Build server side filter from entities ui specialized component
     */
    buildDashboardFilter(entities, risk = null, pestle = null, days = null, language = null) {
        let catFilter = {};
        if (entities.type === ENTITIES_FILTER_ALL_ENTITIES) {
            catFilter.allCategories = true;
            catFilter.allUnassigned = true;
        } else if (entities.type === ENTITIES_FILTER_SPECIFIC) {
            if (entities.entitiesId.length) {
                catFilter.riskEntityIds = entities.entitiesId;
            } else {
                catFilter.allCategories = true;
                catFilter.allUnassigned = true;
            }
        } else {
            if (entities.categories && entities.categories.allCategories) {
                catFilter.allCategories = true;
            }
            if (entities.categories && entities.categories.allUnassigned) {
                catFilter.allUnassigned = true;
            }
            catFilter.categoryIds = entities.categories.categoriesId;
        }
        catFilter.level = risk;
        catFilter.pestle = pestle;
        catFilter.noOfDays = days;
        if (language) {
            let languageObj = [];
            forIn(language, (value, key) => {
                if (value) languageObj.push(key)
            });
            catFilter.languages = languageObj;
        }
        return catFilter;
    },

    /**
     * Generates an 'array of languages' filter, using the reduxStore languageFilter object
     */
    extractLanguagesFilter(languagesObj) {
        let languageFilter = [];
        forIn(languagesObj, (value, key) => {
            if (value) {
                languageFilter.push(key)
            }
        });

        return languageFilter;
    },

    languageArrayToObject(langArray) {
        let langObj = {};
        APP_LANGUAGES.forEach(language => {
            langObj[language] = langArray.indexOf(language) > -1;
        })
        return langObj
    },

    /**
     * Generate the single select value for the categories
     */

    getSingleCategoryFilter(entities, categoryStore) {
        if (entities.type === ENTITIES_FILTER_ALL_ENTITIES) {
            return "defaultAllCategories";
        } else if (entities.type === ENTITIES_FILTER_SPECIFIC) {
            return "defaultAllCategories";
        } else {
            if (categoryStore && entities.categories.categoriesId) {
                // validate category still exists
                let matchNo = 0;
                let firstFound = 'defaultAllCategories';
                for (let i=0 ; i< entities.categories.categoriesId.length; i++) {
                    if (categoryStore[entities.categories.categoriesId[i]]) {
                        matchNo++;
                        if (matchNo === 1) {
                            firstFound = entities.categories.categoriesId[i];
                        }
                    }
                }
                if (matchNo > 1) {
                    return "defaultAllCategories";
                } else {
                    return firstFound;
                }
            }
            return "defaultAllCategories";
        }
    },

    setCategoryPersistenceFilter(value) {
        if (value === 'defaultAllCategories') {
            commonActions.updateUserFilters({
                entitiesFilter: {
                    type: "all_entities",
                    entitiesId: [],
                    categories: {
                        allCategories: true,
                        allUnassigned: true,
                        categoriesId: []
                    }
                }
            });
        } else {
            commonActions.updateUserFilters({
                entitiesFilter: {
                    type: "categories",
                    entitiesId: [],
                    categories: {
                        allCategories: false,
                        allUnassigned: false,
                        categoriesId: [value]
                    }
                }
            });
        }
    },

    getDocHeight() {
        var D = document;
        return Math.max(
            D.body.scrollHeight, D.documentElement.scrollHeight,
            D.body.offsetHeight, D.documentElement.offsetHeight,
            D.body.clientHeight, D.documentElement.clientHeight
        );
    },

    processSanctionReport(report) {
        // console.log(report)
        let el = document.createElement( 'html' );
        el.innerHTML = `<html>${report}</html>`;

        let title = el.getElementsByTagName("title")[0].innerText.trim();
        if (title === 'SANCTN') {
            title = SANCTION_TYPE_SANLST_TITLE;
        }

        // Date
        let loadDate = null
        if (el.getElementsByClassName("PUB-DATE")[0]) {
            loadDate = el.getElementsByClassName("PUB-DATE")[0].innerText.replace("LOAD-DATE", "Load Date");
        }

        if (el.getElementsByClassName("DATE")[0]) {
            loadDate = el.getElementsByClassName("DATE")[0].innerText.replace("DATE:", "");
        }

        // Sanction type
        const publicationName = el.getElementsByTagName("meta");

        let publicationType = null
        for (let i = 0; i < publicationName.length; i++) {
            if (publicationName[i].name === "sourceName") {
                if (publicationName[i].content === SANCTION_TYPE_SANLST) {
                    publicationType = SANCTION_TYPE_SANLST_SANCTIONS
                } else {
                    publicationType = publicationName[i].content
                }
            }
        }

        // Excerpt (short content) for tables
        let excerpt = null
        switch (publicationType) {
            case SANCTION_TYPE_PEP_ES:
            case SANCTION_TYPE_PEP:
                if (el.getElementsByClassName("PERSONAL")[0]) {
                    excerpt = el.getElementsByClassName("PERSONAL")[0].innerText;
                }
                break;
            case SANCTION_TYPE_WATCHB:
                if (el.getElementsByClassName("OTH-OFFCL-INFO")[0]) {
                    excerpt = el.getElementsByClassName("OTH-OFFCL-INFO")[0].innerText;
                }
                break;
            case SANCTION_TYPE_SANLST:
            case SANCTION_TYPE_SANCTION_LISTS:
            case SANCTION_TYPE_ES_EMBARGOS:
            case SANCTION_TYPE_ES_FRONTEIRA_INTERNATIONAL:
            default:
                if (el.getElementsByClassName("PARTY-INFO")[0]) {
                    excerpt = el.getElementsByClassName("PARTY-INFO")[0].innerText
                }
                break
        }

        // Publication Source
        let publication = null

        if (el.getElementsByClassName("SOURCE")[0]) {
            publication = el.getElementsByClassName("SOURCE")[0].innerText
        }

        if (excerpt && excerpt.length) {
            excerpt = excerpt.substr(0, 264) + "...";
        }

        return { html: report, title, type: publicationType, publication, excerpt, loadDate}
    },


    processEsSanctionReport(report, title, publicationType, publication, loadDate, id) {
        // console.log(report)
        let el = document.createElement( 'html' );
        el.innerHTML = `<html>${report}</html>`;

        let res = utils.parseBySections(el);

        // Excerpt (short content) for tables
        let excerpt = res["CASE:"];
        // console.log("computed Excerpt: ", excerpt);

        if (excerpt && excerpt.length) {
            excerpt = excerpt.substr(0, 264) + "...";
        }

        let pub = "SOURCE COUNTRY: " + res["COUNTRY:"];

        return { html: report, title: title, type: res["CATEGORY:"], publication: pub, excerpt, loadDate, id}
    },


    parseBySections(htmlElement) {
        let result = {};
        htmlElement.querySelectorAll('section').forEach(section => {
            section.querySelectorAll("bodytext > h").forEach(line => {
                const field = line.innerText.trim();
                if (line.parentNode.querySelectorAll("p").length) {
                    const value = line.parentNode.querySelectorAll("p")[0].innerText.trim();
                    result = {
                        ...result,
                        [field]: value
                    }
                }
            })
        });
        htmlElement.querySelectorAll('organizationName').forEach(section => {
            result = {
                ...result,
                ["organizationName"]: section.innerText.trim()
            }
        });
        htmlElement.querySelectorAll('date').forEach(section => {
            result = {
                ...result,
                ["date"]: section.innerText.trim()
            }
        });
        let street = "";
        let city = "";
        let state = "";
        let postalCode = "";
        let country = "";
        let county = "";
        htmlElement.querySelectorAll('address').forEach(section => {
            section.querySelectorAll("street").forEach(line => {
                street += line.innerText.trim() + ", ";
            });
            section.querySelectorAll("city").forEach(line => {
                city += line.innerText.trim() + ", ";
            });
            section.querySelectorAll("state").forEach(line => {
                state += line.innerText.trim() + ", ";
            });
            section.querySelectorAll("postalCode").forEach(line => {
                postalCode += line.innerText.trim() + ", ";
            });
            section.querySelectorAll("country").forEach(line => {
                country += line.innerText.trim() + ", ";
            });
            section.querySelectorAll("county").forEach(line => {
                county += line.innerText.trim() + ", ";
            });
        });
        result = {
            ...result,
            ["address"]: street + postalCode + city + county + state + country
        };
        return result;
    },


    parseByClasses(htmlElement, classSelectors) {
        let result = {};
        Object.keys(classSelectors).forEach(className => {
            const item = htmlElement.querySelectorAll(`.${className}`);
            if (item.length) {
                result = {
                    ...result,
                    ...classSelectors[className](item[0])
                }
            }
        });
        return result;
    },

    initializeTracking(appState) {
        window.pendo.initialize({
            visitor: {
                id: appState.user.userId ? appState.user.userId : 'unknown-user-id',
                // name: appState.user.name,
                custom: null,
                interfaceLanguage: appState.user.preferences.language,
                appVersion: appState.header.version
            },

            account: {
                id: appState.user.customerName ? appState.user.customerName : 'unknown-account-id'
                // name:         // Optional
                // planLevel:    // Optional
                // planPrice:    // Optional
                // creationDate: // Optional

                // You can add any additional account level key-values here,
                // as long as it's not one of the above reserved names.
            }
        });
    },

    isElementTopInViewport: (el) => {
        let rect = el.getBoundingClientRect();

        return rect.top >= 0;
    },

    mockPromise: (response = true, failureRate = 3) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                if (random(0, 10) > (10 - failureRate)) {
                    console.log("Reject");
                    reject("Reject");
                } else {
                    console.log("OK");
                    resolve(response);
                }
            }, 2000);
        });
    },

    /**
     * Usage: attach a local function to the tooltip parent as 'onMouseEnter={this.thisComponentsTooltipHandlerEnter}';
     * The local function then calls the util, like this:
     * thisComponentsTooltipHandlerEnter(e) {utils.attachTooltipToCursor(e, -20, 25)}
     *
     * Finds the first 'tooltip' class element, and adds position fixed to it
     * On mouse enter tooltip parent, it will calculate window x and y (plus/minus offsets) and attach inline styles to
     * the tooltip to follow the cursor position
     *
     * @param   {event} e
     * @param   {number} offsetY in px (positive or negative) - space  between cursor and tooltip, starting point is
     *     top left corner of tooltip container
     * @param   {number} offsetX in px (positive or negative) - tooltip positioning on the x axis, starting point is
     *     top left corner of tooltip container
     *
     */
    attachTooltipToCursor(e, offsetX, offsetY) {
        let tooltipParentElement = e.currentTarget,
            tooltip              = tooltipParentElement.getElementsByClassName('tooltip')[0];
        tooltip.style.position = 'fixed';
        tooltipParentElement.onmousemove = function(e) {
            //get the cursor x and y positions and inline-style the tooltip (tooltip is position: fixed)
            tooltip.style.left = (e.clientX + offsetX) + 'px';
            tooltip.style.top = (e.clientY + offsetY) + 'px';
        };
    },

    getDisplayName(WrappedComponent) {
        return WrappedComponent.displayName || WrappedComponent.name || 'Component';
    },

    getTermsAndConditionsLink(interfaceLanguage) {
        let link = "https://www.lexisnexis.com/en-us/terms/general/default.page";

        switch (interfaceLanguage) {
            case "fr":
                link = "https://www.lexisnexis.com/terms/lngp/gnb/fr/default.asp";
                break;
            case "de":
                link = "https://www.lexisnexis.com/terms/lngp/de/de_terms_include.htm";
                break;
            default:
                link = "https://www.lexisnexis.com/en-us/terms/general/default.page";
                break;
        }

        return link;
    },

    checkCategoryLinks(categoryId) {
        return new Promise((resolve, reject) => {
            Promise.all([categoriesApi.getCategoriesAlertsCount(categoryId), categoriesApi.getCategoriesFeedsCount(categoryId)]).then(
                responses => {
                    const linksCount = responses[0].body + responses[1].body;
                    if (linksCount > 0 && linksCount <= CATEGORY_DELETION_LINKED_MAX_COUNT) {
                        Promise.all([
                            categoriesApi.getCategoriesAlerts(categoryId),
                            categoriesApi.getCategoriesFeeds(categoryId)
                        ]).then(listResponses => {
                            const alertsList = listResponses[0].body.map(item => { return { name: item.name, type: LINK_TYPE_ALERT } });
                            const feedsList = listResponses[1].body.map(item => { return { name: item.title, type: LINK_TYPE_FEED } });
                            const list = [ ...alertsList, ...feedsList ].sort((a, b) => toLower(a.name) > toLower(b.name) ? 1 : -1 )
                            resolve({ count: linksCount, list });
                        })
                    } else {
                        resolve({ count: linksCount, list: null });
                    }
            }).catch(e => {
                reject(e);
            })
        })
    },

    filterConvertToPref(filters) {
        return {
            entitiesFilter: filters.entities,
            pestleFilter: filters.pestle,
            riskFilter: filters.risk,
            daysFilter: filters.days,
            languageFilter: filters.language
        }
    },

    filterConvertFromPref(filters) {
        return {
            entities: filters.entitiesFilter,
            pestle: filters.pestleFilter,
            risk: filters.riskFilter,
            days: filters.daysFilter,
            language: filters.languageFilter
        }
    },

    getUrlQueryParam(name, url) {
        if (!url) {
            url = window.location.href;
        }
        name = name.replace(/[\[\]]/g, '\\$&');
        var regex   = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
            results = regex.exec(url);
        if (!results) {
            return null;
        }
        if (!results[2]) {
            return '';
        }
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    },

    getRiskSubjects(subjects, filter = true, visiblePerspectives = VISIBLE_RISK_PERSPECTIVES) {
        let risks = [];

        if (subjects && subjects.length) {
            subjects.forEach(subject => {
                subject.risks.forEach(subjectRisk => {
                    const risk = {
                        score: subjectRisk.riskScore,
                        short: subjectRisk.riskScore[0],
                        riskFactor: subjectRisk.riskFactor,
                        perspective: subjectRisk.riskPerspective
                    }
                    if (!filter || visiblePerspectives.indexOf(subjectRisk.riskPerspective) > -1) {
                        risks.push(risk);
                    }
                });
            })
        }

        return risks;
    },

    urlParamsToLoadParams(urlParams) {
        return {
            filters: { riskAngle: urlParams.riskAngle, riskLevel: urlParams.riskLevel, language: urlParams.language },
            sorting: { sortBy: urlParams.sortBy, direction: urlParams.direction }
        }
    },

    loadParamsToUrlParams(loadParams) {
        return {
            ...loadParams.sorting,
            ...loadParams.filters
        }
    },

    convertNewDossierDataToOldFormat(newDossierData) {
        return {
            "iSearchResponse": {
                "name": newDossierData.CompanyName,
                "entityId": newDossierData.DossierId,
                "attributeMap": {
                    "Zip": newDossierData.PostalCode,
                    "Google+URL": newDossierData.GooglePlusUrl,
                    "Ticker": newDossierData.Ticker,
                    "NumEmployees": newDossierData.NumbEmployees,
                    "E-mailAddress": newDossierData.CompanyEmail,
                    "LinkedInURL": newDossierData.LinkedinUrl,
                    "CompanyType": newDossierData.CompanyType,
                    "City": newDossierData.City,
                    "EntityID": newDossierData.DossierId,
                    "CompanyName": newDossierData.CompanyName,
                    "TwitterURL": newDossierData.TwitterUrl,
                    "StreetAddress": newDossierData.StreetAddress,
                    "FortuneRanking": newDossierData.FortuneRanking,
                    "State": newDossierData.State,
                    "FaxNumber": newDossierData.FaxNumber,
                    "WebAddress": newDossierData.WebAddress,
                    "PhoneNumber": newDossierData.CompanyPhoneNumber,
                    "Country": newDossierData.RegionsCountry,
                    "PrimaryNAICS": "<primary_naics><primary_desc><![CDATA[Software Publishers]]></primary_desc><primary_num>" + newDossierData.AllNaicList[0] + "</primary_num></primary_naics>",
                    "PrimarySIC": "<primary_sic><primary_desc><![CDATA[Prepackaged Software]]></primary_desc><primary_num>" + newDossierData.AllSicList[0] + "</primary_num></primary_sic>",
                    "exchanges": newDossierData.StockExchangeList.join(", "),
                    "FacebookURL": newDossierData.FacebookUrl,
                    "PinterestURL": newDossierData.PinterestUrl,
                    "YouTubeURL": newDossierData.YoutubeUrl,
                }
            },
            "iDunsNumber":	newDossierData.iDunsNumber
        };
    }

};

export default utils;



// WEBPACK FOOTER //
// ./src/scripts/utils/utilities.js